import React from 'react';
import { createPortal } from 'react-dom';
import { Send, Loader, X as XIcon, ChevronDown } from 'lucide-react';
import { useGetRolesQuery } from '../../redux/api/authApi';

export interface InviteFormData {
  email: string;
  role: string;
  expires_in_days: number;
}

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<InviteFormData>;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData,
}) => {
  const { data: roles = [], isLoading: isLoadingRoles } = useGetRolesQuery(undefined, {
    skip: !isOpen, // Only fetch when modal is open
  });

  const [formData, setFormData] = React.useState<InviteFormData>({
    email: initialData?.email || '',
    role: initialData?.role || (roles.length > 0 ? roles[0].value : 'tenant_member'),
    expires_in_days: initialData?.expires_in_days || 7,
  });

  // Update role when roles are loaded
  React.useEffect(() => {
    if (roles.length > 0 && !formData.role) {
      setFormData((prev) => ({ ...prev, role: roles[0].value }));
    }
  }, [roles, formData.role]);

  React.useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  React.useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      email: '',
      role: roles.length > 0 ? roles[0].value : 'tenant_member',
      expires_in_days: 7,
    });
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center"
      style={{ 
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="bg-engramiq-dark-blue border border-engramiq-green/30 rounded-lg p-6 w-full max-w-xl mx-4 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ 
          zIndex: 100000,
          position: 'relative',
          backgroundColor: '#0d1830',
          width: '100%',
          maxWidth: '36rem',
        }}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-engramiq-bright-grey hover:text-engramiq-light-grey transition-colors"
          disabled={isLoading}
        >
          <XIcon className="w-5 h-5" />
        </button>
        
        <h3 className="text-xl font-bold text-engramiq-light-grey mb-4">Invite Member</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-engramiq-bright-grey mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              placeholder="user@example.com"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-engramiq-bright-grey mb-1">Role *</label>
            <div className="relative">
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 pr-10 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none appearance-none"
                disabled={isLoading || isLoadingRoles}
              >
                {isLoadingRoles ? (
                  <option>Loading roles...</option>
                ) : roles.length === 0 ? (
                  <>
                    <option value="tenant_member">TENANT_MEMBER</option>
                    <option value="tenant_admin">TENANT_ADMIN</option>
                  </>
                ) : (
                  roles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))
                )}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-light-grey pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-engramiq-bright-grey mb-1">Expires In (Days)</label>
            <input
              type="number"
              value={formData.expires_in_days}
              onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) || 7 })}
              min="1"
              max="30"
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              disabled={isLoading}
            />
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 bg-surface-800 border border-engramiq-green/30 hover:border-engramiq-green rounded-lg text-engramiq-light-grey transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-white transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render modal using portal to document.body to avoid z-index/overflow issues
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  
  if (!portalTarget) return null;

  return createPortal(modalContent, portalTarget);
};

export default InviteMemberModal;

